import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError, mediaUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSiteContent } from "../context/SiteContentContext";
import { Avatar } from "../components/Avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Settings as SettingsIcon, Ban, Loader2, AlertTriangle, Volume2, Upload, Play, RotateCcw, Mail, Lock, Columns2, ShieldCheck, ImagePlus } from "lucide-react";
import { toast } from "sonner";

const GENDERS = ["Cisgender Female", "Cisgender Male", "Trans-Woman", "Trans-Man", "Non-Binary", "Queer", "Intersex"];

const SOUND_PRESETS = [
  { name: "Leopard Roar", url: "", note: "default" },
  { name: "Meow", url: "/sounds/meow.mp3" },
  { name: "Purr", url: "/sounds/purr.mp3" },
];

export default function Settings() {
  const navigate = useNavigate();
  const { user, logout, patchUser, refreshUser } = useAuth();
  const { content } = useSiteContent();
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({ name: "", dob: "", gender: "", location: "", hide_friends: false });
  const [blocked, setBlocked] = useState([]);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [emailNotif, setEmailNotif] = useState(false);
  const [emailDigest, setEmailDigest] = useState(true);
  const [profilePrivate, setProfilePrivate] = useState(false);
  const [classicLayout, setClassicLayout] = useState(false);
  const [customSound, setCustomSound] = useState("");
  const [soundUploading, setSoundUploading] = useState(false);
  const [legalReq, setLegalReq] = useState({ requested_legal_name: "", message: "", document_url: "", uploading: false });
  const docRef = useRef();
  const soundFileRef = useRef();
  const previewRef = useRef();

  const load = async () => {
    const { data } = await api.get(`/users/${user.id}`);
    setMe(data);
    setForm({ name: data.name, dob: data.about_me?.birthday || "", gender: data.about_me?.gender || "", location: data.about_me?.location || "", hide_friends: data.hide_friends || false });
    setSoundEnabled(data.sound_enabled !== false);
    setEmailNotif(data.email_notifications === true);
    setProfilePrivate(data.profile_private === true);
    setClassicLayout(data.classic_layout === true);
    setCustomSound(data.notification_sound_url || "");
    const b = await api.get("/account/blocked"); setBlocked(b.data);
  };
  useEffect(() => { load(); }, []);

  const toggleSound = async (v) => {
    setSoundEnabled(v);
    try { await api.put("/users/me", { sound_enabled: v }); patchUser({ sound_enabled: v }); toast.success(v ? "Notification sounds on" : "Notification sounds muted"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); setSoundEnabled(!v); }
  };
  const toggleEmailNotif = async (v) => {
    setEmailNotif(v);
    try { await api.put("/users/me", { email_notifications: v }); patchUser({ email_notifications: v }); toast.success(v ? "Email alerts on" : "Email alerts off"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); setEmailNotif(!v); }
  };
  const toggleEmailDigest = async (v) => {
    setEmailDigest(v);
    try { await api.put("/users/me", { email_digest: v }); patchUser({ email_digest: v }); toast.success(v ? "Weekly recap on" : "Weekly recap off"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); setEmailDigest(!v); }
  };
  const toggleProfilePrivate = async (v) => {
    setProfilePrivate(v);
    try { await api.put("/users/me", { profile_private: v }); patchUser({ profile_private: v }); toast.success(v ? "Your profile is now private" : "Your profile is now public"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); setProfilePrivate(!v); }
  };
  const toggleClassicLayout = async (v) => {
    setClassicLayout(v);
    try { await api.put("/users/me", { classic_layout: v }); patchUser({ classic_layout: v }); toast.success(v ? "Classic layout on" : "Classic layout off"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); setClassicLayout(!v); }
  };
  const uploadSound = async (e) => {
    const f = e.target.files?.[0]; if (!f) return; e.target.value = "";
    if (!f.type.startsWith("audio/")) { toast.error("Please choose an audio file (MP3, WAV, OGG)."); return; }
    if (f.size > 2 * 1024 * 1024) { toast.error("Sound must be under 2MB. Try a shorter clip."); return; }
    setSoundUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put("/users/me", { notification_sound_url: data.url });
      patchUser({ notification_sound_url: data.url }); setCustomSound(data.url);
      toast.success("Your notification sound is set!");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSoundUploading(false); }
  };
  const selectPreset = async (url) => {
    try { await api.put("/users/me", { notification_sound_url: url }); patchUser({ notification_sound_url: url }); setCustomSound(url); toast.success(url ? "Sound updated" : "Reverted to the default Spotter sound"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const previewSound = (explicit) => {
    try {
      const src = explicit !== undefined ? explicit : customSound;
      const finalSrc = src || content.notification_sound_url;
      if (!finalSrc) return;
      if (!previewRef.current) previewRef.current = new Audio();
      previewRef.current.src = mediaUrl(finalSrc); previewRef.current.currentTime = 0; previewRef.current.volume = 0.7;
      previewRef.current.play().catch(() => toast.error("Tap again — your browser blocked autoplay."));
    } catch (e) {}
  };
  const isCustomUpload = customSound && !SOUND_PRESETS.some((p) => p.url === customSound);

  const save = async () => {
    try {
      const { data } = await api.put("/users/me", { name: form.name, dob: form.dob, gender: form.gender, location: form.location, hide_friends: form.hide_friends });
      patchUser({ ...data, name: data.name });
      toast.success(data.name_pending ? "Profile saved — name change sent for admin approval" : "Settings saved");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const unblock = async (id) => { await api.post("/friends/unblock", { to_user_id: id }); setBlocked(blocked.filter((b) => b.id !== id)); toast.success("Unblocked"); };
  const onDocPick = async (e) => {
    const f = e.target.files?.[0]; if (!f) return; e.target.value = "";
    setLegalReq((r) => ({ ...r, uploading: true }));
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLegalReq((r) => ({ ...r, document_url: data.url, uploading: false }));
      toast.success("Document attached");
    } catch (err) { setLegalReq((r) => ({ ...r, uploading: false })); toast.error(apiError(err.response?.data?.detail)); }
  };
  const submitLegalReq = async () => {
    if (!legalReq.requested_legal_name.trim()) { toast.error("Enter your new legal name."); return; }
    try {
      const { data } = await api.post("/legal-name-requests", { requested_legal_name: legalReq.requested_legal_name, message: legalReq.message, document_url: legalReq.document_url });
      toast.success(data.message || "Request submitted");
      setLegalReq({ requested_legal_name: "", message: "", document_url: "", uploading: false });
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const deactivate = async () => { if (!window.confirm("Deactivate your account? Log back in any time to reactivate.")) return; await api.post("/account/deactivate"); logout(); navigate("/"); };
  const del = async () => { if (!window.confirm("Permanently DELETE your account? This cannot be undone.")) return; await api.delete("/account"); logout(); navigate("/"); };

  if (!me) return <div className="flex justify-center py-10"><Loader2 className="w-8 h-8 animate-spin text-spotter-maroon" /></div>;

  return (
    <div className="max-w-2xl mx-auto">
      <h2 className="font-display text-3xl font-bold text-spotter-maroon mb-5 flex items-center gap-2"><SettingsIcon className="w-7 h-7" />Settings</h2>

      <div className="bg-white border-2 border-black rounded-xl p-4 space-y-3 mb-5 brand-shadow-sm">
        <h3 className="font-heading font-bold text-lg">Account</h3>
        <div><Label>Display name <span className="text-xs text-spotter-maroon">(changes require admin approval)</span></Label><Input data-testid="settings-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border-2 border-black" /></div>
        <div><Label>Birthdate</Label><Input data-testid="settings-dob" type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} className="border-2 border-black w-full min-w-0 max-w-full appearance-none block" /></div>
        <div>
          <Label>Gender</Label>
          <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
            <SelectTrigger data-testid="settings-gender" className="border-2 border-black bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>{GENDERS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Location</Label><Input data-testid="settings-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="border-2 border-black" /></div>
        <div className="flex items-center justify-between"><span className="text-sm font-semibold">Hide my extra Pride (Top Spots always show)</span><Switch data-testid="settings-hide-friends" checked={form.hide_friends} onCheckedChange={(v) => setForm({ ...form, hide_friends: v })} /></div>
        <Button data-testid="settings-save" onClick={save} className="bg-spotter-maroon text-spotter-cream border-2 border-black">Save changes</Button>
      </div>

      <div className="bg-white border-2 border-black rounded-xl p-4 space-y-3 mb-5 brand-shadow-sm" data-testid="settings-legal-card">
        <h3 className="font-heading font-bold text-lg flex items-center gap-2"><ShieldCheck className="w-5 h-5" />Legal name</h3>
        <div>
          <Label>Your legal name</Label>
          <Input data-testid="settings-legal-name" value={me.about_me?.legal_name || ""} disabled readOnly placeholder="Not set" className="border-2 border-black bg-gray-100 text-gray-500 cursor-not-allowed" />
          <p className="text-xs text-spotter-maroon mt-1">You can't change your legal name yourself. To update it, submit a request below — Admins review within 24–48 hours with proof.</p>
        </div>
        <div className="space-y-2 bg-spotter-cream border-2 border-black rounded-lg p-3" data-testid="legal-name-request">
          <Label className="text-sm font-bold">Request a legal name change</Label>
          <Input data-testid="legal-req-name" value={legalReq.requested_legal_name} onChange={(e) => setLegalReq({ ...legalReq, requested_legal_name: e.target.value })} placeholder="Your new legal name" className="border-2 border-black" />
          <textarea data-testid="legal-req-message" value={legalReq.message} onChange={(e) => setLegalReq({ ...legalReq, message: e.target.value })} placeholder="Briefly explain the change (required)" className="w-full border-2 border-black rounded-md p-2 text-sm min-h-[60px]" />
          <div className="text-xs text-spotter-maroon bg-white border border-black rounded p-2">
            <b>Accepted documents:</b> court/legal name-change document, marriage certificate, driver's license or state ID (with address &amp; ID number redacted), passport photo page (number redacted).
            <br /><b className="text-red-700">Do NOT upload debit/credit cards or any info beyond proof of your name.</b>
          </div>
          <input ref={docRef} type="file" accept="image/*,application/pdf" hidden onChange={onDocPick} />
          <div className="flex items-center gap-2">
            <Button type="button" size="sm" variant="outline" data-testid="legal-req-upload" onClick={() => docRef.current?.click()} disabled={legalReq.uploading} className="border-2 border-black gap-1 text-xs">
              {legalReq.uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}{legalReq.document_url ? "Document attached ✓" : "Attach document"}
            </Button>
            <Button type="button" size="sm" data-testid="legal-req-submit" onClick={submitLegalReq} className="bg-spotter-maroon text-spotter-cream border-2 border-black text-xs">Submit request</Button>
          </div>
        </div>
      </div>


      <div className="bg-white border-2 border-black rounded-xl p-4 space-y-2 mb-5 brand-shadow-sm" data-testid="settings-layout-card">
        <h3 className="font-heading font-bold text-lg flex items-center gap-2"><Columns2 className="w-5 h-5" />Classic Social Media Layout</h3>
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold">Show my profile in a two-column layout on desktop — About, song &amp; Top Spots on the left; photos, videos, Pride, Scratch Post &amp; posts on the right.</span>
          <Switch data-testid="settings-classic-layout-toggle" checked={classicLayout} onCheckedChange={toggleClassicLayout} />
        </div>
        <p className="text-xs text-spotter-maroon">On phones your profile always stacks in a single column.</p>
      </div>

      <div className="bg-white border-2 border-black rounded-xl p-4 space-y-2 mb-5 brand-shadow-sm" data-testid="settings-privacy-card">
        <h3 className="font-heading font-bold text-lg flex items-center gap-2"><Lock className="w-5 h-5" />Private profile</h3>
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold">Make my profile private — non-friends see only my profile picture and cover photo</span>
          <Switch data-testid="settings-private-toggle" checked={profilePrivate} onCheckedChange={toggleProfilePrivate} />
        </div>
        <p className="text-xs text-spotter-maroon">Your Pride, plus the Owner, Admins and Mods, can always see your full profile.</p>
      </div>

      <div className="bg-white border-2 border-black rounded-xl p-4 space-y-2 mb-5 brand-shadow-sm" data-testid="settings-email-card">
        <h3 className="font-heading font-bold text-lg flex items-center gap-2"><Mail className="w-5 h-5" />Email notifications</h3>
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold">Also email me when I get a notification (Pride requests, tags, comments, reactions, etc.)</span>
          <Switch data-testid="settings-email-toggle" checked={emailNotif} onCheckedChange={toggleEmailNotif} />
        </div>
        <p className="text-xs text-spotter-maroon">Direct messages are never emailed — they only show in Spot Chat.</p>
        <div className="flex items-center justify-between pt-2 border-t-2 border-black/10">
          <span className="text-sm font-semibold">📬 Send me a weekly recap email (your statuses + new activity)</span>
          <Switch data-testid="settings-digest-toggle" checked={emailDigest} onCheckedChange={toggleEmailDigest} />
        </div>
      </div>

      <div className="bg-white border-2 border-black rounded-xl p-4 space-y-3 mb-5 brand-shadow-sm" data-testid="settings-sound-card">
        <h3 className="font-heading font-bold text-lg flex items-center gap-2"><Volume2 className="w-5 h-5" />Notification sounds</h3>
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold">Play a sound for every notification &amp; message</span>
          <Switch data-testid="settings-sound-toggle" checked={soundEnabled} onCheckedChange={toggleSound} />
        </div>
        <p className="text-sm text-spotter-maroon">
          {isCustomUpload ? "Using your uploaded sound." : "Choose a built-in sound or upload your own."}
        </p>
        <div className="flex flex-wrap gap-2" data-testid="sound-presets">
          {SOUND_PRESETS.map((p) => {
            const active = (customSound || "") === p.url;
            return (
              <div key={p.name} className={`flex items-center gap-1 border-2 border-black rounded-full pl-3 pr-1 py-1 ${active ? "bg-spotter-maroon text-spotter-cream" : "bg-spotter-cream"}`}>
                <button data-testid={`sound-preset-${p.name.toLowerCase().replace(/\s+/g,'-')}`} onClick={() => selectPreset(p.url)} className="text-sm font-bold">
                  {p.name}{p.note ? ` (${p.note})` : ""}{active ? " ✓" : ""}
                </button>
                <button data-testid={`sound-preview-${p.name.toLowerCase().replace(/\s+/g,'-')}`} onClick={() => previewSound(p.url)} className={`rounded-full p-1 ${active ? "hover:bg-white/20" : "hover:bg-black/10"}`} aria-label={`Preview ${p.name}`}><Play className="w-3.5 h-3.5" /></button>
              </div>
            );
          })}
        </div>
        <input ref={soundFileRef} type="file" accept="audio/*" hidden onChange={uploadSound} />
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" data-testid="settings-sound-preview" onClick={() => previewSound()} className="border-2 border-black gap-1"><Play className="w-4 h-4" />Preview current</Button>
          <Button size="sm" data-testid="settings-sound-upload" onClick={() => soundFileRef.current?.click()} disabled={soundUploading} className="bg-spotter-maroon text-spotter-cream border-2 border-black gap-1">
            {soundUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}Upload my own
          </Button>
          {isCustomUpload && <Button size="sm" variant="outline" data-testid="settings-sound-reset" onClick={() => selectPreset("")} className="border-2 border-black gap-1"><RotateCcw className="w-4 h-4" />Use default</Button>}
        </div>
        <p className="text-xs text-spotter-maroon/70">MP3, WAV or OGG · keep it short (under 2MB).</p>
      </div>

      <div className="bg-white border-2 border-black rounded-xl p-4 mb-5 brand-shadow-sm">
        <h3 className="font-heading font-bold text-lg mb-2 flex items-center gap-2"><Ban className="w-5 h-5" />Blocked members</h3>
        {blocked.length === 0 ? <p className="text-sm text-spotter-maroon">You haven't blocked anyone.</p> : blocked.map((b) => (
          <div key={b.id} className="flex items-center gap-3 py-1" data-testid={`blocked-${b.id}`}>
            <Avatar user={b} size="sm" />
            <span className="flex-1 text-sm font-semibold">{b.name}</span>
            <Button size="sm" variant="outline" data-testid={`unblock-${b.id}`} onClick={() => unblock(b.id)} className="border-2 border-black">Unblock</Button>
          </div>
        ))}
      </div>

      <div className="bg-red-50 border-2 border-red-700 rounded-xl p-4 brand-shadow-sm">
        <h3 className="font-heading font-bold text-lg mb-2 text-red-700 flex items-center gap-2"><AlertTriangle className="w-5 h-5" />Danger Zone</h3>
        <div className="flex gap-2">
          <Button data-testid="deactivate-btn" onClick={deactivate} variant="outline" className="border-2 border-black">Deactivate</Button>
          <Button data-testid="delete-account-btn" onClick={del} className="bg-red-700 text-white border-2 border-black">Delete my account</Button>
        </div>
      </div>
    </div>
  );
}
